import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface QmsDashboard {
  readonly releaseRef: string | null;
  readonly plans: { total: number; byStatus: Record<string, number> };
  readonly cases: { total: number; byStatus: Record<string, number> };
  readonly executions: { total: number; pass: number; fail: number; blocked: number; passPct: number; failPct: number; blockedPct: number };
  readonly defects: { total: number; open: number; byStatus: Record<string, number>; bySeverity: Record<string, number> };
  readonly uat: { total: number; byStatus: Record<string, number> };
  readonly certifications: { total: number; byStatus: Record<string, number> };
  readonly metricBreaches: number;
}

/**
 * M34 — Test Management Dashboard (§31 / §24). Read-only management roll-up
 * across the QMS: test-plan / test-case / execution / defect / UAT /
 * certification counters plus the execution pass/fail/blocked rates, open-defect
 * tally and metric-breach counter. Optionally scoped to a single release (LAW 4
 * tenant isolation is enforced server-side).
 */
export function TestManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<QmsDashboard | null>(null);
  const [releaseRef, setReleaseRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = releaseRef ? `?releaseRef=${encodeURIComponent(releaseRef)}` : '';
    apiRequest<{ data: QmsDashboard }>(`/v1/qms/dashboard${qs}`, { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, releaseRef]);

  return (
    <main aria-label="qms-dashboard">
      <h1>{t('m34.tmd.title')}</h1>
      <div className="filters">
        <label>
          {t('m34.common.release')}
          <input aria-label="filter-release" value={releaseRef} onChange={(e) => setReleaseRef(e.target.value)} placeholder={t('m34.tmd.allReleases')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? <p>{t('m34.common.loading')}</p> : (
        <section aria-label="qms-metric-cards" className="cards">
          <article aria-label="card-plans"><h2>{t('m34.tmd.cardPlans')}</h2><p aria-label="plans-total">{data.plans.total}</p></article>
          <article aria-label="card-cases"><h2>{t('m34.tmd.cardCases')}</h2><p aria-label="cases-total">{data.cases.total}</p></article>
          <article aria-label="card-executions">
            <h2>{t('m34.tmd.cardExecutions')}</h2>
            <p aria-label="executions-total">{data.executions.total}</p>
            <p aria-label="executions-pass-pct">{t('m34.tmd.passPct', { pct: data.executions.passPct })}</p>
          </article>
          <article aria-label="card-defects">
            <h2>{t('m34.tmd.cardDefects')}</h2>
            <p aria-label="defects-total">{data.defects.total}</p>
            <p aria-label="defects-open">{t('m34.tmd.open', { n: data.defects.open })}</p>
          </article>
          <article aria-label="card-uat"><h2>{t('m34.tmd.cardUat')}</h2><p aria-label="uat-total">{data.uat.total}</p></article>
          <article aria-label="card-certifications"><h2>{t('m34.tmd.cardCertifications')}</h2><p aria-label="certifications-total">{data.certifications.total}</p></article>
          <article aria-label="card-breaches"><h2>{t('m34.tmd.cardBreaches')}</h2><p aria-label="metric-breaches">{data.metricBreaches}</p></article>
        </section>
      )}
    </main>
  );
}
